import React from "react";
import Button from "../../../components/common/button";
import { useOrganization } from "../../../hooks/useOrganization";
import { Organization } from "@/types/models";
import ErrorPopup from "../../../components/common/errorPopup";
import OnboardingCard from "../../../components/common/OnboardingCard";
import Header from "../../../components/common/Header";
import PreviewCard from "../../../components/common/PreviewCard";

interface LinkedinURLProps {
  onNext: (orgData?: Organization) => void;
  currentStep: number;
  totalSteps: number;
  initialUrl: string;
  onBack: () => void;
}

const LinkedinURL: React.FC<LinkedinURLProps> = ({ 
  onNext, 
  currentStep, 
  totalSteps, 
  initialUrl, 
  onBack 
}) => {
  const [linkedinUrl, setLinkedinUrl] = React.useState(initialUrl);
  const [error, setError] = React.useState('');
  const { updateOrganization, isUpdating, organization } = useOrganization();
  const [showError, setShowError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  React.useEffect(() => {
    if (initialUrl) {
      setLinkedinUrl(initialUrl);
    }
  }, [initialUrl]);

  const validateLinkedInUrl = (url: string) => {
    const linkedInRegex = /^https?:\/\/(www\.)?linkedin\.com\/company\/[\w-]+(\/?|\/.+)$/;
    return linkedInRegex.test(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!linkedinUrl.trim()) {
      setErrorMessage('Please enter a LinkedIn URL');
      setShowError(true);
      return;
    }

    if (!validateLinkedInUrl(linkedinUrl.trim())) {
      setErrorMessage('Please enter a valid LinkedIn company profile URL');
      setShowError(true);
      return;
    }

    try {
      if (linkedinUrl !== initialUrl) {
        const updatedOrg = await updateOrganization({ linkedin_profile_url: linkedinUrl.trim() });
        onNext(updatedOrg);
      } else {
        if (!organization?.name) {
          throw new Error('Organization name is required');
        }
        onNext({ ...organization, linkedin_profile_url: linkedinUrl.trim() });
      }
    } catch (error) {
      console.error('Failed to update LinkedIn URL:', error);
      setErrorMessage('Failed to verify LinkedIn URL. Please try again.');
      setShowError(true);
    }
  };

  React.useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        try {
          let updatedOrg;
          if (linkedinUrl && linkedinUrl !== initialUrl) {
            updatedOrg = await updateOrganization({ 
              linkedin_profile_url: linkedinUrl.trim() 
            });

            if (updatedOrg) {
              setLinkedinUrl(updatedOrg.linkedin_profile_url || '');
            }
          }
        } catch (error) {
          console.error('Failed to save LinkedIn URL on back:', error);
          setErrorMessage('Failed to save changes. Your progress may be lost.');
          setShowError(true);
        } finally {
          onBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onBack, linkedinUrl, initialUrl, updateOrganization]);

  const leftPanelContent = (
    <div className="flex flex-col h-full">
      <div className="flex flex-col px-[16px] pt-[4px] pb-[16px]">
        <div className="mb-1">
          <div className="text-[32px] font-semibold mb-[36px] text-left">Let's get to know your business better!</div>

          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="space-y-3">
              <div className="relative">
                <label className="text-sm font-normal text-[#75777C] mb-2 block text-left">
                  Company's LinkedIn profile
                </label>
                <input
                  type="text"
                  value={linkedinUrl}
                  onChange={(e) => {
                    setLinkedinUrl(e.target.value);
                    setError('');
                  }}
                  placeholder="Paste your URL here (e.g., https://linkedin.com/company/your-company)"
                  className="w-full h-[40px] pl-4 pr-4 text-left rounded-[10px] text-[#222222] text-sm placeholder:text-[#9FA1A7] focus:outline-none focus:ring-1 focus:ring-[#29DC5B] focus:border-transparent shadow-[0px_0px_0px_1px_#EEEFF1_inset]"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-[40px] bg-[#29DC5B] mt-[28px] text-[#222222] rounded-[10px] flex items-center justify-center text-sm font-normal hover:bg-[#22c55e] transition-colors shadow-[0px_0px_0px_1px_#2325291A_inset]"
              disabled={isUpdating}
            >
              {isUpdating ? 'Verifying...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen h-screen flex flex-col items-center">
      <OnboardingCard 
        leftPanel={leftPanelContent}
        rightPanel={<PreviewCard 
          variant="preview" 
          companyName={organization?.linkedin_company_profile?.name || organization?.name}
          showRole={false}
        />}
        pageInfo={{
          currentPage: currentStep + 1,
          totalPages: totalSteps
        }}
      />
      
      <ErrorPopup
        isOpen={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default LinkedinURL;
